import type { ContentType } from '@/@types/common'
import type { Notification } from '@/@types/notification'
import ClickableStackAvatarGroup from '@/components/ClickableStackAvatarGroup'
import ScreenNames from '@/components/ScreenNames'
import { CONTENT_LOCALE } from '@/constants/locale'
import day from '@/utils/day'
import useNotificationClickHandler from '@/utils/hooks/useNotificationClickHandler'
import { css } from '@emotion/react'
import { LikeIcon } from '@ruguoapp/jike-fe-icons'
import type { HTMLAttributes } from 'react'
import NotificationContainer from './NotificationContainer'
import NotificationReference from './NotificationReference'

export interface LikeNotificationProps extends HTMLAttributes<HTMLDivElement> {
	data: Notification
	type: ContentType
}

export default function LikeNotification({
	data,
	type: contentType,
	...props
}: LikeNotificationProps) {
	const {
		actionItem: { users, usersCount },
		referenceItem,
		createdAt,
	} = data
	const { handleClick, isClickable } = useNotificationClickHandler(data)

	return (
		<NotificationContainer
			onClick={handleClick}
			isClickable={isClickable}
			css={css`
				display: grid;
				grid-template-columns: 1fr auto;
				gap: 20px;
			`}
			{...props}
		>
			<div
				css={css`
					display: flex;
					flex-direction: column;
					gap: 15px;
				`}
			>
				<header
					css={css`
						display: flex;
						align-items: center;
						gap: 12px;
					`}
				>
					<LikeIcon
						css={css`
							width: 16px;
							height: 16px;
							color: var(--tint-secondary);
						`}
					/>
					<div
						css={css`
							line-height: 24px;
						`}
					>
						<ScreenNames data={users.slice(0, 3)} />
						<span
							css={css`
								color: var(--tint-secondary);
							`}
						>
							{usersCount > 3 && <span>等{usersCount}人</span>}
							赞了你的{CONTENT_LOCALE[contentType]}
						</span>
					</div>
				</header>

				<main>
					<ClickableStackAvatarGroup data={users} isMoreIconShow={usersCount > users.length} />
				</main>
				<footer
					css={css`
						color: var(--tint-tertiary);
						font-size: 12px;
						line-height: 18px;
					`}
				>
					{day(createdAt).fromNow()}
				</footer>
			</div>

			<NotificationReference data={referenceItem} />
		</NotificationContainer>
	)
}
